import React, { useState } from "react";
import { ChevronDown, ChevronRight, Loader2, Plus } from "lucide-react";
import { Button } from "../../../components/ui/button";
import {
	getFormTemplates,
	cloneFormTemplate,
} from "../constants/form-templates";
import { supabase } from "@/lib/supabase/supabaseClient";
import { SupabaseTableEnum } from "@/lib/supabase/supabaseTypes";
import { useCentralStore } from "../../../store/Central";
import { showNotification } from "../../../store/Central/selectors";
import { useNavigate } from "react-router-dom";
import { Json } from "@/lib/supabase/supabaseTypesImport";
import { getLocalStorage, setLocalStorage } from "@/lib/utils/local-storage";

const BG_COLOR_MAP: Record<string, string> = {
	"blue-50": "bg-blue-50 border-blue-200",
	"amber-50": "bg-amber-50 border-amber-200",
	"green-50": "bg-green-50 border-green-200",
	"violet-50": "bg-violet-50 border-violet-200",
};

const ACCENT_COLOR_MAP: Record<string, string> = {
	"blue-50": "bg-blue-400",
	"amber-50": "bg-amber-400",
	"green-50": "bg-green-400",
	"violet-50": "bg-violet-400",
};

interface FormTemplatesSectionProps {
	formCount: number;
}

export const FormTemplatesSection: React.FC<FormTemplatesSectionProps> = ({
	formCount,
}) => {
	const organizationId = useCentralStore((state) => state.organization?.id);
	const navigate = useNavigate();
	const [cloningId, setCloningId] = useState<string | null>(null);

	const defaultCollapsed = formCount >= 2;
	const storedCollapsed = getLocalStorage<string | null>(
		"dentlab-forms-templates-collapsed",
		null
	);
	const [collapsed, setCollapsed] = useState(
		storedCollapsed !== null ? storedCollapsed === "true" : defaultCollapsed
	);

	const templates = getFormTemplates();

	const handleToggle = () => {
		const next = !collapsed;
		setCollapsed(next);
		setLocalStorage("dentlab-forms-templates-collapsed", String(next));
	};

	const handleClone = async (templateIndex: number) => {
		if (!organizationId) return;

		const template = templates[templateIndex];
		setCloningId(template.title);

		try {
			const cloned = cloneFormTemplate(template, organizationId);

			const { data, error } = await supabase
				.from(SupabaseTableEnum.FORMS)
				.insert({
					title: cloned.title,
					description: cloned.description,
					bg_color: cloned.bg_color,
					organization_id: cloned.organization_id,
					definition: cloned.definition as unknown as Json,
					rules: cloned.rules as unknown as Json[],
				})
				.select()
				.single();

			if (error) throw error;

			showNotification({
				message: `"${template.title}" wurde als Entwurf erstellt`,
				type: "success",
			});

			navigate(`/auftragsformulare/${data.id}`);
		} catch (err) {
			showNotification({
				message: "Fehler beim Erstellen der Vorlage",
				type: "error",
			});
		} finally {
			setCloningId(null);
		}
	};

	const fieldCount = (definition: (typeof templates)[0]["definition"]) =>
		definition.sections.reduce((sum, s) => sum + s.fields.length, 0);

	return (
		<div className="mb-6">
			<button
				onClick={handleToggle}
				className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3 hover:text-gray-900 transition-colors"
				aria-label="Vorlagen ein-/ausblenden"
				data-testid="form-templates-toggle"
			>
				{collapsed ? (
					<ChevronRight className="h-4 w-4" />
				) : (
					<ChevronDown className="h-4 w-4" />
				)}
				Vorlagen entdecken
			</button>

			{!collapsed && (
				<div className="flex gap-3 overflow-x-auto pb-2">
					{templates.map((template, idx) => (
						<div
							key={template.title}
							className={`flex-shrink-0 w-64 rounded-lg border p-4 flex flex-col justify-between ${
								BG_COLOR_MAP[template.bg_color] ??
								"bg-gray-50 border-gray-200"
							}`}
						>
							<div>
								<div className="flex items-center gap-2 mb-2">
									<div
										className={`w-2 h-2 rounded-full ${
											ACCENT_COLOR_MAP[
												template.bg_color
											] ?? "bg-gray-400"
										}`}
									/>
									<h4 className="text-sm font-medium text-gray-900">
										{template.title}
									</h4>
								</div>
								<p className="text-xs text-gray-600 line-clamp-1 mb-3">
									{template.description}
								</p>
								<div className="flex gap-3 text-xs text-gray-500">
									<span>
										{template.definition.sections.length}{" "}
										Abschnitte
									</span>
									<span>
										{fieldCount(template.definition)} Felder
									</span>
								</div>
							</div>
							<Button
								variant="outline"
								size="sm"
								className="mt-3 w-full gap-1.5"
								onClick={() => handleClone(idx)}
								disabled={cloningId !== null}
								aria-label={`Vorlage ${template.title} übernehmen`}
								data-testid={`clone-template-${idx}`}
							>
								{cloningId === template.title ? (
									<Loader2 className="h-3.5 w-3.5 animate-spin" />
								) : (
									<Plus className="h-3.5 w-3.5" />
								)}
								Übernehmen
							</Button>
						</div>
					))}
				</div>
			)}
		</div>
	);
};
